export function parseHTMLtoFragment(html: string): DocumentFragment {
  if (!html || typeof html !== 'string') {
    // return empty fragment if no html is provided
    return document.createDocumentFragment()
  }
  try {
    // Use the more secure template method if available
    const template = document.createElement('template')
    template.innerHTML = html
    return template.content
  } catch {
    // Fallback to range method
    const range = document.createRange()
    range.selectNode(document.body)
    return range.createContextualFragment(html)
  }
}

let fragmentToStringXMLSerializer: XMLSerializer = null
export function fragmentToString(fragment: DocumentFragment): string {
  if (!fragment || Node.DOCUMENT_FRAGMENT_NODE !== fragment.nodeType) {
    // return empty string if no fragment is provided
    return ''
  }
  try {
    // Use the more secure template method if available
    const template = document.createElement('template')
    template.content.appendChild(fragment.cloneNode(true))
    return template.innerHTML
  } catch {
    // Fallback to XMLSerializer method
    const serializer = fragmentToStringXMLSerializer || (fragmentToStringXMLSerializer = new XMLSerializer())
    return serializer.serializeToString(fragment)
  }
}

export const DEFAULT_ALLOWED_TAGS = ['html', 'body', 'style', 'div', 'span', 'p', 'br']

export const DEFAULT_FORBIDDEN_TAGS = ['script', 'style', 'head', 'meta']

export const DEFAULT_ALLOWED_ATTRIBUTES = ['style', 'class', 'id', 'dir', 'type']

export function sanitizeHTML(
  html: string,
  options: {
    allowedTags?: string[]
    allowedAttributes?: string[]
    includeDefault?: boolean
    forbiddenTags?: string[]
  } = {}
): string {
  const {
    allowedTags = [],
    allowedAttributes = [],
    includeDefault = true,
    forbiddenTags = DEFAULT_FORBIDDEN_TAGS
  } = options || {}

  const allAllowedTags = allowedTags.map(tag => tag.toLowerCase()).concat(includeDefault ? DEFAULT_ALLOWED_TAGS : [])
  const allAllowedAttributes = allowedAttributes
    .map(attr => attr.toLowerCase())
    .concat(includeDefault ? DEFAULT_ALLOWED_ATTRIBUTES : [])
  const allForbiddenTags = forbiddenTags.map(tag => tag.toLowerCase())

  const fragment = parseHTMLtoFragment(html)

  // sanitize the fragment by replacing all not allowed tag names with span tags and removing script tags
  const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_ELEMENT)
  walker.nextNode()
  let currentNode = walker.currentNode
  while (currentNode) {
    let nextNode = walker.nextNode()
    try {
      // check if the current node is an element
      if (Node.ELEMENT_NODE !== currentNode.nodeType) {
        currentNode = nextNode
        continue
      }
      const node = currentNode as HTMLElement
      const nodeName = node.tagName?.toLowerCase?.() || ''
      const nodeAttributes = Array.from(node.attributes || [])
      if (allForbiddenTags.includes(nodeName)) {
        // Remove forbidden tags
        node.parentNode?.removeChild(node)
        currentNode = nextNode
        continue
      } else if (!allAllowedTags.includes(nodeName)) {
        // Replace not allowed tags with span tags
        const replacement = document.createElement('span')
        replacement.innerHTML = node.innerHTML
        for (const attr of nodeAttributes) {
          const attrName = attr.name.toLowerCase()
          if (allAllowedAttributes.includes(attrName)) {
            replacement.setAttribute(attr.name, attr.value)
          }
        }
        node.replaceWith(replacement)
        nextNode = replacement
        walker.currentNode = replacement
      }
      // Sanitize attributes
      for (const attr of nodeAttributes) {
        const attrName = attr.name.toLowerCase()
        const attrValue = attr.value.toLocaleLowerCase().trim()
        if (
          !allAllowedAttributes.includes(attrName) ||
          attrValue.startsWith('javascript:') ||
          attrValue.startsWith('data:')
        ) {
          node.removeAttribute(attr.name)
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error while sanitizing HTML:', error)
      return ''
    }
    currentNode = nextNode
  }

  return fragmentToString(fragment)
}
